import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { UserContext, decodeToken } from './UserContext';
import NavBar        from './components/NavBar';
import Login         from './components/Login';
import MapView       from './components/MapView';
import HousingDetail from './components/HousingDetail';
import RackView      from './components/RackView';
import CassetteView  from './components/CassetteView';
import QRPrintPage   from './components/QRPrintPage';
import MaterialLabelsPage from './components/MaterialLabelsPage';
import MaterialScan   from './components/MaterialScan';
import MaterialsView  from './components/MaterialsView';
import MaterialCheckout from './components/MaterialCheckout';
import MaterialDropoff  from './components/MaterialDropoff';
import OpsView       from './components/OpsView';
import WorkItemDetail from './components/WorkItemDetail';
import Dashboard     from './components/Dashboard';
import DesignQueue   from './components/DesignQueue';
import AdminUsers        from './components/AdminUsers';
import MyWork            from './components/MyWork';
import FieldManagerView  from './components/FieldManagerView';

// Register service worker for Web Push notifications
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => {});
  });
}

// Pages that don't show the NavBar
const NO_NAV_PATHS = ['/scan/', '/print/', '/material/', '/material-labels/', '/material-checkout/', '/material-dropoff/'];

function App() {
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [user, setUser]   = useState(() => {
    const stored = localStorage.getItem('user');
    if (stored) return JSON.parse(stored);
    const t = localStorage.getItem('token');
    return t ? decodeToken(t) : null;
  });

  const handleLogin = (userData) => {
    setToken(localStorage.getItem('token'));
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
  };

  if (!token) return <Login onLogin={handleLogin} />;

  // Role-based default landing page
  const defaultPath = () => {
    if (!user) return '/';
    if (user.role === 'design') return '/design';
    if (user.role === 'management') return '/dashboard';
    if (user.role === 'managing_director' || user.role === 'director') return '/dashboard';
    if (user.role === 'field_manager') return '/field-manager';
    return '/';
  };

  return (
    <UserContext.Provider value={user}>
      <BrowserRouter>
        <AppShell user={user} onLogout={handleLogout} defaultPath={defaultPath()} />
      </BrowserRouter>
    </UserContext.Provider>
  );
}

function AppShell({ user, onLogout, defaultPath }) {
  const path = window.location.pathname;
  const showNav = !NO_NAV_PATHS.some(p => path.startsWith(p));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
      {showNav && <NavBar onLogout={onLogout} />}
      <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <Routes>
          {/* Map */}
          <Route path="/"             element={<MapView onLogout={onLogout} />} />
          <Route path="/rack/:id"     element={<RackView />} />
          <Route path="/housing/:id"  element={<CassetteView />} />
          {/* QR scan — no nav */}
          <Route path="/scan/:uuid"   element={<HousingDetail />} />
          <Route path="/print/:id"    element={<QRPrintPage />} />
          <Route path="/material/:uuid" element={<MaterialScan />} />
          <Route path="/material-labels/:shipmentId" element={<MaterialLabelsPage />} />
          <Route path="/material-checkout/:uuid" element={<MaterialCheckout />} />
          <Route path="/material-dropoff/:locationId" element={<MaterialDropoff />} />
          {/* Phase 3 */}
          <Route path="/my-work"      element={<MyWork />} />
          <Route path="/ops"          element={<OpsView />} />
          <Route path="/work/:id"     element={<WorkItemDetail />} />
          <Route path="/dashboard"    element={<Dashboard />} />
          <Route path="/design"       element={<DesignQueue />} />
          <Route path="/team"           element={<AdminUsers />} />
          <Route path="/admin"          element={<AdminUsers />} />
          <Route path="/field-manager"  element={<FieldManagerView />} />
          <Route path="/materials"      element={<MaterialsView />} />
          {/* Fallback */}
          <Route path="*"             element={<Navigate to={defaultPath} />} />
        </Routes>
      </div>
    </div>
  );
}

export default App;
